import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { TRUST } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { motion } from "framer-motion";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileLock2,
  Globe2,
  KeyRound,
  Lock,
  Mail,
  ScrollText,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Link } from "wouter";
import { submitForm, SubmitFormError } from "@/lib/submit-form";
import { track } from "@/lib/analytics";
import { isPersonalEmail, WORK_EMAIL_MESSAGE } from "@/lib/email-validation";

const reportSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z
    .string()
    .email("Valid work email is required")
    .refine((v) => !isPersonalEmail(v), WORK_EMAIL_MESSAGE),
  company: z.string().min(2, "Company is required"),
  role: z.string().min(2, "Job title is required"),
  consent: z.boolean().refine((v) => v === true, {
    message: "Please confirm this is for a security review.",
  }),
});

type ReportValues = z.infer<typeof reportSchema>;

function statusStyle(status: string): {
  className: string;
  Icon: typeof CheckCircle2;
} {
  if (status === "Aligned" || status === "In place") {
    return {
      className:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
      Icon: CheckCircle2,
    };
  }
  if (status.toLowerCase().includes("progress")) {
    return {
      className:
        "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
      Icon: Clock,
    };
  }
  return {
    className:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    Icon: ShieldCheck,
  };
}

export default function TrustPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ReportValues>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      role: "",
      consent: false,
    },
  });

  async function onSubmit(values: ReportValues) {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitForm("trust-report-request", values);
      track("marketing_form_submitted", {
        source: "trust-report-request",
        company: values.company,
      });
      setIsSuccess(true);
      form.reset();
    } catch (err) {
      if (err instanceof SubmitFormError && err.fieldErrors) {
        for (const [name, message] of Object.entries(err.fieldErrors)) {
          form.setError(name as keyof ReportValues, {
            type: "server",
            message,
          });
        }
      }
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Layout>
      <SEOHead title={TRUST.seo.title} description={TRUST.seo.description} />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-border/50 bg-slate-50 dark:bg-navy/30 relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {TRUST.eyebrow}
            </div>
            <h1
              className="text-display mb-6 text-navy dark:text-white"
              data-testid="trust-hero-title"
            >
              {TRUST.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {TRUST.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#request"
                className="inline-flex items-center gap-2 rounded-md bg-brand-gradient text-white border-0 px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
                data-testid="trust-jump-to-form"
              >
                Request our security review packet
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#sub-processors"
                className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                See sub-processors
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Compliance posture */}
      <section className="py-20 md:py-24" id="compliance">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12">
              <h2 className="text-h2 text-navy dark:text-white mb-3">
                Compliance posture
              </h2>
              <p className="text-body text-slate-600 dark:text-slate-400 max-w-3xl">
                What we can commit to today. Statuses are precise on
                purpose — "Aligned" means the controls are in place and
                in our DPA; "Examination in progress" means an external
                audit is underway right now.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {TRUST.compliance.map((item) => {
                const { className, Icon } = statusStyle(item.status);
                return (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-border bg-card p-6 flex flex-col gap-4"
                    data-testid={`trust-compliance-${item.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-h4 text-navy dark:text-white">
                        {item.label}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${className}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {item.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Encryption */}
      <section
        className="py-20 md:py-24 bg-slate-50 dark:bg-navy/30 border-y border-border/50"
        id="encryption"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12 flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-300 shrink-0">
                <Lock className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-h2 text-navy dark:text-white mb-3">
                  {TRUST.encryption.title}
                </h2>
                <p className="text-body text-slate-600 dark:text-slate-400 max-w-3xl">
                  {TRUST.encryption.body}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TRUST.encryption.items.map((item, i) => {
                const Icon =
                  i === 0 ? Globe2 : i === 1 ? FileLock2 : KeyRound;
                return (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-border bg-card p-6"
                  >
                    <Icon className="h-6 w-6 text-indigo-500 mb-4" />
                    <h3 className="text-h4 text-navy dark:text-white mb-2">
                      {item.label}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Identity & access */}
      <section className="py-20 md:py-24" id="identity">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12 flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-300 shrink-0">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-h2 text-navy dark:text-white mb-3">
                  {TRUST.identity.title}
                </h2>
                <p className="text-body text-slate-600 dark:text-slate-400 max-w-3xl">
                  {TRUST.identity.body}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TRUST.identity.items.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-border bg-card p-6"
                >
                  <ShieldCheck className="h-6 w-6 text-cyan-500 mb-4" />
                  <h3 className="text-h4 text-navy dark:text-white mb-2">
                    {item.label}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Data residency */}
      <section
        className="py-20 md:py-24 bg-slate-50 dark:bg-navy/30 border-y border-border/50"
        id="residency"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12 flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-300 shrink-0">
                <Globe2 className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-h2 text-navy dark:text-white mb-3">
                  {TRUST.residency.title}
                </h2>
                <p className="text-body text-slate-600 dark:text-slate-400 max-w-3xl">
                  {TRUST.residency.body}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TRUST.residency.regions.map((region) => (
                <div
                  key={region.code}
                  className="rounded-2xl border border-border bg-card p-6"
                  data-testid={`trust-region-${region.code.toLowerCase()}`}
                >
                  <div className="text-3xl font-bold text-navy dark:text-white mb-2">
                    {region.code}
                  </div>
                  <div className="text-sm font-semibold text-foreground mb-1">
                    {region.label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {region.availability}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sub-processors */}
      <section className="py-20 md:py-24" id="sub-processors">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-10">
              <h2 className="text-h2 text-navy dark:text-white mb-3">
                {TRUST.subProcessors.title}
              </h2>
              <p className="text-body text-slate-600 dark:text-slate-400 max-w-3xl">
                {TRUST.subProcessors.body}
              </p>
              <p className="text-sm text-slate-700 dark:text-slate-300 max-w-3xl mt-3 font-medium">
                {TRUST.subProcessors.note}
              </p>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border">
              <table
                className="w-full text-left text-sm"
                data-testid="trust-subprocessors-table"
              >
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Provider</th>
                    <th className="px-4 py-3 font-semibold">Country</th>
                    <th className="px-4 py-3 font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {TRUST.subProcessors.items.map((item) => (
                    <tr key={item.category} className="bg-card">
                      <td className="px-4 py-4 font-semibold text-foreground align-top whitespace-nowrap">
                        {item.category}
                      </td>
                      <td className="px-4 py-4 text-foreground align-top">
                        {item.provider}
                      </td>
                      <td className="px-4 py-4 text-muted-foreground align-top">
                        {item.country}
                      </td>
                      <td className="px-4 py-4 text-muted-foreground align-top">
                        {item.purpose}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Audit log + Privacy split */}
      <section
        className="py-20 md:py-24 bg-slate-50 dark:bg-navy/30 border-y border-border/50"
        id="audit"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-border bg-card p-8">
              <ScrollText className="h-7 w-7 text-cyan-500 mb-4" />
              <h2 className="text-h3 text-navy dark:text-white mb-3">
                {TRUST.audit.title}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {TRUST.audit.body}
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-8">
              <ShieldCheck className="h-7 w-7 text-indigo-500 mb-4" />
              <h2 className="text-h3 text-navy dark:text-white mb-3">
                {TRUST.privacy.title}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {TRUST.privacy.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request form */}
      <section className="py-20 md:py-24" id="request">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-10 text-center">
              <h2 className="text-h2 text-navy dark:text-white mb-3">
                {TRUST.reportRequest.title}
              </h2>
              <p className="text-body text-slate-600 dark:text-slate-400">
                {TRUST.reportRequest.subtitle}
              </p>
            </div>

            <div className="bg-card border rounded-3xl p-8 md:p-10 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-1 bg-brand-gradient" />

              {isSuccess ? (
                <div
                  className="text-center py-12"
                  data-testid="trust-form-success"
                >
                  <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-6">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">
                    {TRUST.reportRequest.successTitle}
                  </h3>
                  <p className="text-muted-foreground mb-8">
                    {TRUST.reportRequest.successBody}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setIsSuccess(false)}
                    data-testid="trust-form-reset"
                  >
                    Send another request
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-5"
                    data-testid="trust-report-form"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First name</FormLabel>
                            <FormControl>
                              <Input placeholder="Jane" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last name</FormLabel>
                            <FormControl>
                              <Input placeholder="Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Work email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="jane@company.com"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company</FormLabel>
                            <FormControl>
                              <Input placeholder="Acme Corp" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Job title</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Security Engineer"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="consent"
                      render={({ field }) => (
                        <FormItem className="flex items-start gap-3 space-y-0 rounded-lg border border-border bg-muted/30 p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value === true}
                              onCheckedChange={(checked) =>
                                field.onChange(checked === true)
                              }
                              data-testid="trust-form-consent"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-snug">
                            <FormLabel className="text-sm font-medium">
                              {TRUST.reportRequest.consentLabel}
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    {submitError && (
                      <div
                        role="alert"
                        data-testid="trust-submit-error"
                        className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
                      >
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{submitError}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12 text-base font-semibold bg-brand-gradient text-white border-0 hover:opacity-90"
                      disabled={isSubmitting}
                      data-testid="trust-form-submit"
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : "Request security review packet"}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      {TRUST.reportRequest.privacyNote}
                    </p>
                  </form>
                </Form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Links */}
      <section className="py-16 border-t border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-h3 text-navy dark:text-white mb-6">
              {TRUST.links.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {TRUST.links.items.map((link) => {
                const isComing = "comingSoon" in link && link.comingSoon;
                if (isComing) {
                  return (
                    <div
                      key={link.label}
                      className="flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground"
                    >
                      <Clock className="h-4 w-4" />
                      <span>{link.label}</span>
                    </div>
                  );
                }
                if (link.external) {
                  return (
                    <a
                      key={link.label}
                      href={link.path}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      {link.path.startsWith("mailto:") ? (
                        <Mail className="h-4 w-4 text-indigo-500" />
                      ) : (
                        <ExternalLink className="h-4 w-4 text-indigo-500" />
                      )}
                      <span>{link.label}</span>
                    </a>
                  );
                }
                return (
                  <Link
                    key={link.label}
                    href={link.path}
                    className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <ArrowRight className="h-4 w-4 text-indigo-500" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
